import React, { useEffect, useState } from "react";
import {
  KeyRound,
  ShieldCheck,
  Copy,
  Download,
  RefreshCw,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  fetchPasskeyStatus,
  changePasskey,
  regenerateRecoveryCodes,
  formatTimestampFull,
} from "../services/api";

const field =
  "w-full bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors placeholder-gray-500";

const errorText = (e, fallback) => {
  const code = e && e.data && e.data.code;
  if (code === "bad_credentials") return "Current passkey is wrong.";
  if (code === "weak_passkey") return "Passkey must be at least 6 characters.";
  if (code === "same_passkey")
    return "The new passkey must be different from the current one.";
  return (e && e.data && e.data.message) || (e && e.message) || fallback;
};

/** One-time display of recovery codes with copy + download. */
export const RecoveryCodes = ({ codes, onDone }) => {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* clipboard unavailable */
    }
  };
  const download = () => {
    const blob = new Blob(
      [
        "Xunzhine recovery codes\n",
        "Each code works once. Keep this file somewhere safe.\n\n",
        text,
        "\n",
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "xunzhine-recovery-codes.txt";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="rounded-2xl border-2 border-yellow-500/40 bg-yellow-500/10 p-4 space-y-3">
      <div className="flex items-center gap-2 text-yellow-300 font-semibold text-sm">
        <ShieldCheck className="w-4 h-4" />
        Your recovery codes — shown only once
      </div>
      <p className="text-xs text-yellow-100/80">
        If you ever forget your passkey, one of these codes lets you set a new
        one from the sign-in screen. Each code works once. Save them now; they
        are never shown again.
      </p>
      <div className="grid grid-cols-2 gap-1.5 font-mono text-sm text-cyan-100">
        {codes.map((c) => (
          <div key={c} className="bg-gray-800/70 rounded-lg px-3 py-1.5 tracking-wider">
            {c}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copy}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 text-cyan-200 hover:bg-gray-600 border border-cyan-500/30 flex items-center gap-1.5"
        >
          <Copy className="w-3.5 h-3.5" />
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={download}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 text-cyan-200 hover:bg-gray-600 border border-cyan-500/30 flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" />
          Download .txt
        </button>
        {onDone && (
          <button
            type="button"
            onClick={onDone}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500 text-gray-900 hover:bg-yellow-400 ml-auto"
          >
            I saved them
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Settings → Passkey: change the passkey (current + new ×2, sign out other
 * devices by default) and manage one-time recovery codes. Cloud accounts only.
 */
const PasskeySection = ({ isOpen }) => {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | change | regen
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [codes, setCodes] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    try {
      setStatus(await fetchPasskeyStatus());
    } catch (e) {
      setStatus(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode("idle");
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
    setCodes(null);
    setNotice(null);
    load();
  }, [isOpen]);

  const reset = () => {
    setMode("idle");
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
  };

  const submitChange = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (next !== confirm) {
      setError("The two new passkeys do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await changePasskey({ current, next, signOutOthers });
      setCodes(r.recoveryCodes || []);
      setNotice(
        `Passkey changed${
          r.signedOut ? ` · ${r.signedOut} other device(s) signed out` : ""
        }. Use the new passkey everywhere, including the Android app.`,
      );
      reset();
      load();
    } catch (err) {
      setError(errorText(err, "Could not change the passkey."));
    } finally {
      setBusy(false);
    }
  };

  const submitRegen = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await regenerateRecoveryCodes(current);
      setCodes(r.recoveryCodes || []);
      setNotice("New recovery codes issued — the old ones no longer work.");
      reset();
      load();
    } catch (err) {
      setError(errorText(err, "Could not create recovery codes."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-cyan-300 text-sm font-semibold flex items-center gap-2">
        <KeyRound className="w-4 h-4" />
        Passkey
      </label>

      {status && (
        <p className="text-xs text-gray-400">
          {status.custom ? (
            <>
              Custom passkey set
              {status.updatedAt ? ` on ${formatTimestampFull(status.updatedAt)}` : ""}
              {" · "}
              {status.recoveryRemaining} recovery code
              {status.recoveryRemaining === 1 ? "" : "s"} left.
            </>
          ) : (
            <>
              You are using the passkey configured on the server. Change it here
              to pick your own and get recovery codes for “forgot passkey”.
            </>
          )}
        </p>
      )}

      {codes && (
        <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
      )}

      {notice && !codes && (
        <div className="p-3 rounded-xl border-2 bg-green-600/10 border-green-500/30 text-green-300 text-sm">
          {notice}
        </div>
      )}

      {mode === "idle" && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setMode("change");
            }}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-700 text-cyan-300 hover:bg-gray-600 border-2 border-cyan-500/30 hover:border-cyan-500 flex items-center gap-2"
          >
            <KeyRound className="w-4 h-4" />
            Change passkey
          </button>
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setMode("regen");
            }}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-700 text-yellow-300 hover:bg-gray-600 border-2 border-yellow-500/30 hover:border-yellow-500 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {status && status.custom ? "New recovery codes" : "Get recovery codes"}
          </button>
        </div>
      )}

      {mode !== "idle" && (
        <form
          onSubmit={mode === "change" ? submitChange : submitRegen}
          className="rounded-2xl bg-gray-700/40 border border-gray-600 p-4 space-y-3"
        >
          <div>
            <label className="block text-xs text-cyan-300 font-semibold mb-1">
              Current passkey
            </label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className={field}
              autoFocus
            />
          </div>
          {mode === "change" && (
            <>
              <div>
                <label className="block text-xs text-cyan-300 font-semibold mb-1">
                  New passkey (at least 6 characters)
                </label>
                <div className="relative">
                  <input
                    type={showNext ? "text" : "password"}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    autoComplete="new-password"
                    className={`${field} pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNext((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-300 p-1"
                    aria-label={showNext ? "Hide passkey" : "Show passkey"}
                  >
                    {showNext ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-cyan-300 font-semibold mb-1">
                  Repeat new passkey
                </label>
                <input
                  type={showNext ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className={field}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={signOutOthers}
                  onChange={(e) => setSignOutOthers(e.target.checked)}
                  className="w-4 h-4 accent-cyan-500"
                />
                Sign out my other devices
              </label>
            </>
          )}
          {mode === "regen" && (
            <p className="text-xs text-gray-400">
              This issues 10 fresh one-time codes and invalidates any older
              ones.
            </p>
          )}
          {error && (
            <div role="alert" className="p-3 rounded-xl border-2 bg-red-600/10 border-red-500/30 text-red-300 text-sm">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={
                busy ||
                !current ||
                (mode === "change" && (next.length < 6 || !confirm))
              }
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-yellow-500 text-gray-900 hover:bg-yellow-400 disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {mode === "change" ? "Change passkey" : "Issue codes"}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-600 text-gray-200 hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PasskeySection;
